import { useMemo, useState } from 'react';
import { Alert, Box, Button, Container, Link, Stack, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useAuth } from './AuthContext';
import { type Message, msgKey, renderMessage } from '@/i18n/message';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const FAILED_ATTEMPTS_BEFORE_CAPTCHA = 3;

const loginSchema = z.object({
  email: z.string().min(1, 'login.emailRequired').email('login.emailInvalid'),
  password: z.string().min(1, 'login.passwordRequired'),
  totpCode: z.string().optional(),
  backupCode: z.string().optional(),
  captcha: z.string().optional(),
});

type LoginForm = z.infer<typeof loginSchema>;

interface CaptchaQuestion {
  a: number;
  b: number;
}

function newCaptcha(): CaptchaQuestion {
  return {
    a: Math.floor(Math.random() * 9) + 1,
    b: Math.floor(Math.random() * 9) + 1,
  };
}

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  useDocumentTitle(t('pageTitles.login'));
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [formError, setFormError] = useState<Message | null>(null);
  const [formInfo, setFormInfo] = useState<Message | null>(null);
  const [totpRequired, setTotpRequired] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [captcha, setCaptcha] = useState<CaptchaQuestion>(() => newCaptcha());

  const showCaptcha = failedAttempts >= FAILED_ATTEMPTS_BEFORE_CAPTCHA;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', totpCode: '', backupCode: '', captcha: '' },
  });

  const expectedCaptchaAnswer = useMemo(
    () => String(captcha.a + captcha.b),
    [captcha.a, captcha.b],
  );

  if (!loading && user) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (values: LoginForm): Promise<void> => {
    setFormError(null);
    setFormInfo(null);

    if (showCaptcha && values.captcha !== expectedCaptchaAnswer) {
      setFormError(msgKey('login.captchaWrong'));
      setCaptcha(newCaptcha());
      return;
    }

    try {
      await login({
        email: values.email,
        password: values.password,
        ...(values.totpCode ? { totpCode: values.totpCode } : {}),
        ...(values.backupCode ? { backupCode: values.backupCode } : {}),
      });
      navigate('/', { replace: true });
    } catch (err) {
      if (isAxiosError(err) && err.response) {
        const body = err.response.data as { error?: string } | undefined;
        if (body?.error === 'totp_required') {
          // Pierwsza prośba o kod 2FA — to nie blad, tylko brakujace pole. Pokazuje
          // pole TOTP i neutralny komunikat info (zamiast czerwonego alertu),
          // nie zliczam tej próby do captcha.
          setTotpRequired(true);
          setFormInfo(msgKey('login.totpRequired'));
          return;
        }
      }
      // Wszystkie pozostale przypadki to faktyczny bląd — zliczamy do captcha.
      setFailedAttempts((c) => c + 1);
      if (isAxiosError(err) && err.response) {
        const body = err.response.data as { error?: string } | undefined;
        if (body?.error === 'account_inactive') {
          setFormError(msgKey('login.accountInactive'));
          return;
        }
        if (body?.error === 'patient_web_access_denied') {
          setFormError(msgKey('login.patientNoPanelAccess'));
          return;
        }
        if (err.response.status === 401) {
          setFormError(msgKey('login.invalidCredentials'));
          return;
        }
      }
      setFormError(msgKey('login.unknownError'));
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('login.title')}
      </Typography>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={2}>
          {formError && <Alert severity="error">{renderMessage(formError, t)}</Alert>}
          {formInfo && !formError && (
            <Alert severity="info" role="status">{renderMessage(formInfo, t)}</Alert>
          )}

          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={t('login.email')}
                type="email"
                autoComplete="email"
                fullWidth
                required
                error={!!fieldState.error}
                helperText={fieldState.error ? t(fieldState.error.message ?? '') : ''}
              />
            )}
          />

          <Controller
            name="password"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={t('login.password')}
                type="password"
                autoComplete="current-password"
                fullWidth
                required
                error={!!fieldState.error}
                helperText={fieldState.error ? t(fieldState.error.message ?? '') : ''}
              />
            )}
          />

          {totpRequired && (
            <>
              <Controller
                name="totpCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('login.totpCode')}
                    autoComplete="one-time-code"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
                    fullWidth
                  />
                )}
              />
              <Link component={RouterLink} to="/dev/totp" variant="body2">
                {t('login.devTotpHint')}
              </Link>
            </>
          )}

          {showCaptcha && (
            <Controller
              name="captcha"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('login.captchaPrompt', { a: captcha.a, b: captcha.b })}
                  fullWidth
                  required
                  helperText={t('login.captchaHelp')}
                />
              )}
            />
          )}

          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {t('login.submit')}
          </Button>

          <Link component={RouterLink} to="/forgot-password" variant="body2">
            {t('login.forgotPassword')}
          </Link>
          <Link component={RouterLink} to="/help" variant="body2">
            {t('login.helpCenter')}
          </Link>
        </Stack>
      </Box>
    </Container>
  );
}
